import { useMutation, useQueryClient } from '@tanstack/react-query';
import API, { getCompressedFile } from '../../../context/API';
import { STORES_QUERY_KEY } from './useStores';
import type { CreateStorePayload, UpdateStorePayload, Store } from '../store/userStoresSlice';

type CreateStoreInput = CreateStorePayload & { imageFile?: File | null };
type UpdateStoreInput = UpdateStorePayload & { imageFile?: File | null };

// Every other field still goes over JSON — only the image needs multipart,
// so we only switch encodings when a new file is actually being uploaded.
// The backend's FileInterceptor is registered under the field name 'image'
// (not 'imageUrl' — that field is a plain URL string used only for the
// JSON-body removal path, `imageUrl: null`).
function buildStoreFormData(payload: Record<string, unknown>): FormData {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    // Booleans go through the backend's NormalizeBooleanFieldsPipe, which
    // parses the literal strings 'true'/'false' — an empty string is instead
    // read as "field omitted" and would silently drop a `false` update.
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
}

export const useCreateStore = () => {
  const queryClient = useQueryClient();
  return useMutation<Store, string, CreateStoreInput>({
    mutationFn: async ({ imageFile, ...payload }) => {
      let res;
      if (imageFile) {
        const compressed = await getCompressedFile(imageFile);
        const formData = buildStoreFormData(payload);
        formData.append('image', compressed, compressed.name);
        res = await API.post('/stores', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        res = await API.post('/stores', payload);
      }
      if (res.data.success) return res.data.data;
      throw res.data.message || 'Failed to create store';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
    },
  });
};

export const useUpdateStore = () => {
  const queryClient = useQueryClient();
  return useMutation<Store, string, UpdateStoreInput>({
    mutationFn: async ({ id, imageFile, ...data }) => {
      let res;
      if (imageFile) {
        const compressed = await getCompressedFile(imageFile);
        const formData = buildStoreFormData(data);
        formData.append('image', compressed, compressed.name);
        res = await API.patch(`/stores/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        // Plain JSON PATCH — also how an explicit image removal is sent,
        // via `imageUrl: null` in `data` (see StoreForm).
        res = await API.patch(`/stores/${id}`, data);
      }
      if (res.data.success) return res.data.data;
      throw res.data.message || 'Failed to update store';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
    },
  });
};

export interface ToggleAutoAcceptPayload {
  id: string;
  autoAcceptOrders: boolean;
}

// Lighter-weight than useUpdateStore: the table's inline toggle only ever
// knows the single boolean it's flipping, not the full store form payload
// (name, address, schedules, ...) that UpdateStorePayload requires.
export const useToggleAutoAccept = () => {
  const queryClient = useQueryClient();
  return useMutation<Store, string, ToggleAutoAcceptPayload>({
    mutationFn: async ({ id, autoAcceptOrders }) => {
      const res = await API.patch(`/stores/${id}`, { autoAcceptOrders });
      if (res.data.success) return res.data.data;
      throw res.data.message || 'Failed to update auto-accept setting';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
    },
  });
};

export interface BulkAutoAcceptPayload {
  autoAcceptOrders: boolean;
  // Admin-only: the backend has no other way to scope "all locations" to a
  // single merchant when the caller can see every merchant's stores.
  merchantId?: string;
}

export interface BulkAutoAcceptResult {
  updatedCount: number;
  autoAcceptOrders: boolean;
}

export const useBulkAutoAccept = () => {
  const queryClient = useQueryClient();
  return useMutation<BulkAutoAcceptResult, string, BulkAutoAcceptPayload>({
    mutationFn: async (payload) => {
      const res = await API.put('/stores/auto-accept', payload);
      if (res.data.success) return res.data.data;
      throw res.data.message || 'Failed to update auto-accept setting for all locations';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
    },
  });
};

export const useDeleteStore = () => {
  const queryClient = useQueryClient();
  return useMutation<string, string, string>({
    mutationFn: async (storeId) => {
      const res = await API.delete(`/stores/${storeId}`);
      if (res.data.success) return storeId;
      throw res.data.message || 'Failed to delete store';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STORES_QUERY_KEY });
    },
  });
};
